import { CardContainer } from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  PermissionName,
  TplApplicableFor,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../../auth/RestrictedByDocumentPermission.js";
import { TemplatePlaceholderFillButton } from "../buttons/TemplatePlaceholderFillButton.js";
import {
  CreateItemsFromTemplatesArgs,
  CreateItemsWithProductsArgs,
} from "../hooks/useCreateItem.js";
import { TemplatePlaceholderEmptyStateBox_ItemFragment } from "./TemplatePlaceholderEmptyStateBox.generated.js";

interface Props {
  projectId: string | null;
  docId: string;
  item: TemplatePlaceholderEmptyStateBox_ItemFragment;
  templateApplicableFor: TplApplicableFor;
  isEditable: boolean;
  /** "Fill placeholder" is gated on quote_document_update, same as the item-wizard
   *  button in the same tree row. */
  documentViewerPermissions: PermissionName[];
  onFilled?: (itemId: string) => void;
  refetchWizardQuestions: () => Promise<unknown>;
  createItemsWithProducts: (
    args: CreateItemsWithProductsArgs
  ) => Promise<unknown>;
  createItemsFromTemplates: (
    args: CreateItemsFromTemplatesArgs
  ) => Promise<unknown>;
}

/**
 * Words-first empty state for a template type placeholder that has no content
 * yet: one sentence naming where the content will land, and a single call to
 * action. Deliberately not bracket notation — reviews found brackets read as
 * developer placeholders.
 *
 * Renders nothing once the placeholder has content; filling an already-filled
 * placeholder is a separate flow.
 */
export const TemplatePlaceholderEmptyStateBox = ({
  projectId,
  docId,
  item,
  templateApplicableFor,
  isEditable,
  documentViewerPermissions,
  onFilled,
  refetchWizardQuestions,
  createItemsWithProducts,
  createItemsFromTemplates,
}: Props) => {
  const { t } = useTranslate(["QuoteItem"]);

  const placeholder = item.placeholderForTemplateType;

  // `isTemplatePlaceholderFilled` is checked alongside the child count because
  // a placeholder filled from a template keeps its own type marker, and a
  // template whose root has no children leaves the child count at 0 too —
  // so without this the card would offer to fill an already-filled slot.
  if (
    !placeholder ||
    item.children.length > 0 ||
    item.isTemplatePlaceholderFilled
  )
    return null;

  const templateTypeTitle = placeholder.templateType.title;

  return (
    <CardContainer
      sx={theme => ({
        // Dashed and slightly recessed: this is a slot awaiting content, not a
        // filled section. Matches the dashed markers used in the item tree.
        border: `1px dashed ${theme.palette.grey[400]}`,
        backgroundColor: theme.palette.grey[100],
        boxShadow: "none",
      })}
    >
      <Stack direction="column" spacing={1} sx={{ p: 2 }}>
        <Typography variant="h3">
          {t("This placeholder is empty", { ns: "QuoteItem" })}
        </Typography>
        <Typography variant="body2">
          {t(
            "Pick one of your templates or build it from products — everything you add lands under {templateTypeTitle}.",
            { ns: "QuoteItem", templateTypeTitle }
          )}
        </Typography>
        {isEditable && (
          <RestrictedByDocumentPermissionWithDebug
            permission="quote_document_update"
            document={{ viewerPermissions: documentViewerPermissions }}
          >
            <Stack direction="row" sx={{ pt: 1 }}>
              <TemplatePlaceholderFillButton
                projectId={projectId}
                docId={docId}
                itemId={item.id}
                templateTypeName={templateTypeTitle}
                templateTypeId={placeholder.templateType.id}
                templateSearchFilterDefaults={item.templateSearchFilterDefaults}
                templateSearchSortingDefaults={
                  item.templateSearchSortingDefaults
                }
                templateApplicableFor={templateApplicableFor}
                createItemsWithProducts={createItemsWithProducts}
                createItemsFromTemplates={createItemsFromTemplates}
                onCompleted={onFilled}
                label={t("Fill placeholder", { ns: "QuoteItem" })}
                variant="contained"
                color="primary"
                refetchWizardQuestions={refetchWizardQuestions}
              />
            </Stack>
          </RestrictedByDocumentPermissionWithDebug>
        )}
      </Stack>
    </CardContainer>
  );
};
